import React from "react";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const INFO_TONES = {
  default: {
    color: "#646a8a",
    bgcolor: "#f7f8fb",
    border: "#e3e5ee",
  },
  onDark: {
    color: "rgba(255,255,255,0.95)",
    bgcolor: "rgba(255,255,255,0.2)",
    border: "rgba(255,255,255,0.35)",
  },
  onLight: {
    color: "rgba(22,26,58,0.7)",
    bgcolor: "rgba(22,26,58,0.08)",
    border: "rgba(22,26,58,0.14)",
  },
};

/** Shared dark tooltip look used across ExDashboard KPI/info tips */
export const EXD_TOOLTIP_PROPS = {
  arrow: true,
  placement: "top",
  enterDelay: 150,
  PopperProps: {
    disablePortal: false,
    sx: { zIndex: 10060 },
    modifiers: [
      {
        name: "preventOverflow",
        options: { boundary: "viewport", padding: 8 },
      },
    ],
  },
  componentsProps: {
    tooltip: {
      sx: {
        maxWidth: 320,
        fontSize: 12,
        lineHeight: 1.4,
        color: "#fff",
        bgcolor: "#1b1f4b",
        "& .MuiTooltip-arrow": { color: "#1b1f4b" },
      },
    },
  },
};

export const ExdInfoTip = ({ title, tone = "default", sx = {} }) => {
  if (!title) return null;
  const t = INFO_TONES[tone] || INFO_TONES.default;

  return (
    <Tooltip
      title={title}
      leaveDelay={100}
      describeChild
      {...EXD_TOOLTIP_PROPS}
      enterDelay={200}
    >
      {/* span keeps hover target reliable inside overflow:hidden KPI shells */}
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          flexShrink: 0,
          ml: "auto",
          position: "relative",
          zIndex: 2,
        }}
      >
        <IconButton
          size="small"
          aria-label="info"
          sx={{
            flexShrink: 0,
            width: 22,
            height: 22,
            p: 0,
            color: t.color,
            bgcolor: t.bgcolor,
            border: `1px solid ${t.border}`,
            borderRadius: "50%",
            "&:hover": {
              bgcolor: t.bgcolor,
              opacity: 0.92,
            },
            "& .MuiSvgIcon-root": {
              fontSize: 13,
              color: "inherit",
            },
            ...sx,
          }}
        >
          <InfoOutlinedIcon />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const ghostSx = {
  textTransform: "none",
  fontSize: 12.5,
  fontWeight: 600,
  minHeight: 28,
  px: 1.25,
  borderRadius: "7px",
  borderColor: "#e3e5ee",
  color: "#161a3a",
  bgcolor: "#f7f8fb",
  "&:hover": {
    borderColor: "#2f5bea",
    color: "#2f5bea",
    bgcolor: "#f7f8fb",
  },
};

export const ExdGhostButton = ({ children, onClick, startIcon, sx, ...rest }) => (
  <Button
    type="button"
    size="small"
    variant="outlined"
    onClick={onClick}
    startIcon={startIcon}
    disableElevation
    sx={{ ...ghostSx, ...sx }}
    {...rest}
  >
    {children}
  </Button>
);

export const ExdBackButton = ({ children, onClick, ...rest }) => (
  <ExdGhostButton
    onClick={onClick}
    startIcon={<ArrowBackIcon sx={{ fontSize: "14px !important" }} />}
    {...rest}
  >
    {children}
  </ExdGhostButton>
);

export const ExdPrimaryButton = ({
  children,
  onClick,
  disabled,
  sx,
  ...rest
}) => (
  <Button
    type="button"
    size="small"
    variant="contained"
    onClick={onClick}
    disabled={disabled}
    disableElevation
    sx={{
      textTransform: "none",
      fontSize: 13,
      fontWeight: 700,
      minHeight: 34,
      px: 1.75,
      borderRadius: "8px",
      bgcolor: "#1b1f4b",
      color: "#fff",
      "&:hover": { bgcolor: "#161a3a" },
      "&.Mui-disabled": { bgcolor: "#c5c9da", color: "#fff" },
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Button>
);
